import { heroImages, images } from '../images';
import type { Project } from '../../types/content';

export const miningBuildingsMaintenanceProjects: Project[] = [
  {
    id: 'khoemacau-boxcut-slope',
    title: 'Khoemacau Copper Mine Boxcut Slope Maintenance',
    category: 'mining',
    location: 'Khoemacau Copper Mine',
    client: 'Khoemacau Copper Mine',
    parties: [{ role: 'Employer', name: 'Khoemacau Copper Mine' }],
    summary:
      'Boxcut slope maintenance, sand and vegetation removal, and slope stabilisation at Zone 5 (north, south and central).',
    scope: ['Boxcut slope maintenance', 'Sand and vegetation removal', 'Slope stabilisation at Zone 5: north, south and central'],
    status: 'Listed as ongoing (October 2026 profile)',
    value: 'P3 million',
    images: [{ ...images.miningSlope, caption: 'Representative image of boxcut slopes' }],
    featured: true,
    serviceIds: ['civil'],
    publication: 'published',
  },
  {
    id: 'orapa-tailings-conveyor',
    title: 'Orapa No. 2 Plant Tailings Conveyor',
    category: 'mining',
    location: 'Orapa No. 2 Plant',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary: 'Construction of a tailings conveyor, with civil, structural and electrical / telemetry works (A&I).',
    scope: ['Tailings conveyor construction', 'Civil and structural works', 'Electrical and telemetry works (A&I)'],
    status: 'Completed June 2021',
    value: 'P40.5 million',
    images: [{ ...heroImages.roads, caption: 'Representative image of mine earthworks' }],
    serviceIds: ['civil'],
    publication: 'published',
  },
  {
    id: 'orapa-om1-conveyor-disposal',
    title: 'Disposal of Conveyor Structure, OM1 Tailings Dump',
    category: 'mining',
    location: 'Orapa, Blue Area No. 1 Plant Tailings',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary:
      'Complete dismantling of the conveyor structure and disposal to the Blue Area salvage yard, with associated civil and mechanical works.',
    scope: [
      'Dismantling of conveyor structure and disposal to salvage yard',
      'Removal of mechanical and electrical cables',
      'Bulk earthworks',
      'Construction of a platform and access road to the top of the tailings dump',
    ],
    period: 'August 2018 – December 2018 (4 months)',
    status: 'Completed December 2018',
    value: 'P6.24 million',
    images: [{ ...images.backhoe, caption: 'Representative image of bulk earthworks' }],
    serviceIds: ['civil'],
    publication: 'published',
  },
  {
    id: 'letlhakane-tailings-disposal',
    title: 'Letlhakane Mine Tailings Infrastructure Disposal',
    category: 'mining',
    location: 'Letlhakane Mine',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary: 'Dismantling and disposal of tailings infrastructure at Letlhakane Mine.',
    scope: ['Dismantling of tailings infrastructure', 'Disposal of dismantled infrastructure'],
    status: 'Completed November 2019',
    value: 'P11 million',
    images: [{ ...images.miningSlope, caption: 'Representative image of a mine site' }],
    serviceIds: ['civil'],
    publication: 'published',
  },
  {
    id: 'orapa-ssd-resource-centre',
    title: 'S&SD Resource Centre Earthworks & Paving, Orapa',
    category: 'mining',
    location: 'Orapa Mine, White Area',
    client: 'Debswana Diamond Company',
    parties: [
      { role: 'Employer', name: 'Debswana Diamond Company' },
      { role: 'Subcontracted by', name: 'Team Engineers Botswana' },
    ],
    relationship: 'Liftoff Investments worked as a subcontractor to Team Engineers Botswana.',
    summary:
      'Earthworks, concrete works and paving for the S&SD prefabricated offices, lab offices and conference centre, carried out before the prefabricated offices were installed.',
    scope: ['Earthworks', 'Concrete works before installation of prefabricated offices', 'Paving works'],
    period: 'February 2018 – July 2018 (6 months)',
    status: 'Completed July 2018',
    value: 'P6.8 million',
    images: [{ ...images.plateCompactor, caption: 'Representative image of paving works' }],
    serviceIds: ['civil', 'paving'],
    publication: 'published',
  },
  {
    id: 'karowe-campsite-infrastructure',
    title: 'Karowe Underground Project: 200-Man Campsite Services',
    category: 'mining',
    location: 'Karowe Mine',
    client: 'Lucara Diamond, Karowe Underground Project',
    parties: [{ role: 'Employer', name: 'Lucara Diamond, Karowe Underground Project' }],
    summary: 'Design, supply and installation of roads and water and sewer reticulation for a 200-man campsite.',
    scope: ['Design, supply and installation of roads', 'Water reticulation', 'Sewer reticulation'],
    status: 'Completed',
    value: 'P16,038,474.08',
    images: [{ ...images.backhoe, caption: 'Representative image of reticulation trenching' }],
    serviceIds: ['civil', 'water', 'roadworks'],
    publication: 'published',
  },
  {
    id: 'mphuthe-cjss-hostels',
    title: 'Mphuthe CJSS Hostels, Staff Housing & Academic Buildings',
    category: 'buildings',
    location: 'Letlhakeng',
    client: 'Ministry of Education & Skills Development, Department of Technical Services',
    parties: [
      { role: 'Client', name: 'Ministry of Education & Skills Development, Department of Technical Services' },
      { role: 'Consultant', name: 'Mosienyane & Partners Architects' },
    ],
    summary:
      'Construction of hostels, staff housing, classroom and academic buildings, with associated infrastructure, at Mphuthe Community Junior Secondary School.',
    scope: [
      '2 no. triple-storey hostel blocks',
      '1 no. double-storey six-classroom block',
      '16 no. double-storey staff houses and 3 no. single-storey staff houses',
      '5 no. single-storey academic / classroom buildings',
      'Roads and stormwater reticulation',
      'Potable water supply and sewerage reticulation',
      '2 no. solar farms / calorifiers',
    ],
    reference: 'Contract No. PR 6/1/3/14 (III)',
    status: 'Practical Completion Certificate issued October 2023',
    value: 'P79,779,693.00',
    images: [
      { ...images.buildingWorks, caption: 'Representative image: three-storey hostel block under construction' },
      { ...images.scaffolding, caption: 'Representative image: double-storey staff housing' },
    ],
    featured: true,
    serviceIds: ['building', 'drainage', 'water'],
    publication: 'published',
    reviewNotes: [
      'Project page 8 gives the duration as May 2016 – July 2017, but the references table records practical completion in October 2023. Only the completion milestone is shown.',
      'Replace with the four captioned photographs on page 8 of the profile.',
    ],
  },
  {
    id: 'lobatse-railway-station',
    title: 'Lobatse Railway Station Waiting Rooms & Ticket Office',
    category: 'buildings',
    location: 'Lobatse',
    client: 'Botswana Railways',
    parties: [
      { role: 'Client', name: 'Botswana Railways' },
      { role: 'Consultant', name: 'Gabana Architects (Pty) Ltd' },
    ],
    summary:
      'Rebuilding of the railway station waiting rooms (economy and business class) and ticket office, with car park and external works.',
    scope: [
      'Demolition of existing structures and hauling to an approved dump site',
      'New waiting rooms, ticket office and functional facilities',
      'Stormwater drainage, water reticulation and sewerage reticulation',
      'Interlocking paved access road and car park',
    ],
    period: 'October 2016 – April 2017',
    status: 'Completed August 2017',
    value: 'P2.8 million (project page) / P3.7 million (references table)',
    images: [{ ...images.paving, caption: 'Representative image of a paved access road and station building' }],
    featured: true,
    serviceIds: ['building', 'paving', 'drainage'],
    publication: 'published',
    reviewNotes: ['The profile gives two different values for this project (P2.8M and P3.7M). Confirm which one to publish.'],
  },
  {
    id: 'bmc-lobatse-workshop',
    title: 'BMC Lobatse Workshop, Change Rooms & Boundary Wall',
    category: 'buildings',
    location: 'Botswana Meat Commission, Lobatse',
    client: 'Botswana Meat Commission (BMC)',
    parties: [{ role: 'Client', name: 'Botswana Meat Commission (BMC)' }],
    summary: 'A new steel-framed workshop, staff change rooms and a perimeter wall for the BMC expansion project.',
    scope: [
      '35 m × 18 m workshop with I-section columns, steel frame trusses and IBR roofing',
      'Staff change rooms with ablution amenities',
      '215 m long brickwork and plaster boundary / perimeter wall',
    ],
    period: 'November 2013 – April 2014',
    status: 'Completed April 2014',
    value: 'P3,800,000.00',
    images: [{ ...images.buildingWorks, caption: 'Representative image of brickwork construction' }],
    serviceIds: ['building'],
    publication: 'published',
  },
  {
    id: 'tawana-preschool',
    title: 'Tawana Preschool Classroom Block, Orapa',
    category: 'buildings',
    location: 'Orapa Township',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary: 'Construction of a new classroom block at Tawana Preschool.',
    scope: ['New classroom block'],
    status: 'Completed September 2019',
    value: 'P3.7 million',
    images: [{ ...images.scaffolding, caption: 'Representative image of building works' }],
    serviceIds: ['building'],
    publication: 'published',
  },
  {
    id: 'bowasetso-park',
    title: 'Bowasetso Park Development, Phase 2 Gaborone',
    category: 'buildings',
    location: 'Plot 19894, Phase 2, Gaborone',
    client: 'Debswana Mining Company (Pty) Ltd (Debswana CSI)',
    parties: [{ role: 'Client', name: 'Debswana Mining Company (Pty) Ltd (Debswana CSI)' }],
    summary:
      'Redevelopment of the existing Bowasetso Park. Phase 1 covered the sports field side of the park.',
    scope: [
      'Multi-purpose tennis and basketball courts',
      'Multi-purpose tennis and volleyball courts',
      "Revamped children's playground",
      'Walking / jogging track',
      'Palisade fencing of the sports field',
      'Security area lighting and a park ablution block',
    ],
    period: 'Phase 1: January 2016 – December 2016',
    status: 'Phase 1 complete; Phase 2 anticipated',
    value: 'P2.7 million',
    images: [{ ...images.paving, caption: 'Representative image of paved external works' }],
    serviceIds: ['building', 'paving'],
    publication: 'published',
  },
  {
    id: 'kanye-prison-maintenance',
    title: 'Kanye Prison Facilities Maintenance',
    category: 'maintenance',
    location: 'Kanye',
    client: 'Ministry of Infrastructure & Housing Development, Department of Buildings & Engineering Services',
    parties: [
      {
        role: 'Client',
        name: 'Ministry of Infrastructure & Housing Development, Department of Buildings & Engineering Services',
      },
    ],
    summary:
      'Refurbishment of the existing prison facilities and staff housing, with a new paved access road and a new sewer network served by a containerised treatment plant.',
    scope: [
      'Refurbishment of prison rooms, compound, workshops and facilities',
      'Refurbishment of 24 no. prison staff houses and facilities',
      '749 m prison access road in 80 mm interlocking paving on two layers of gravel base',
      'New sewer reticulation for the staff housing and prison',
      '30 kL containerised wastewater treatment plant',
    ],
    reference: 'Tender No. MIST/MTC/DBES/WOR/62/2014-2015',
    period: 'April 2016 – February 2017',
    status: 'Completed February 2017',
    value: 'P12,437,000.00 plus P2.15 million additional works',
    images: [
      { ...images.paving, caption: 'Representative image: paved access road' },
      { ...images.buildingWorks, caption: 'Representative image: refurbishment works' },
    ],
    featured: true,
    serviceIds: ['building', 'paving', 'water'],
    publication: 'published',
  },
  {
    id: 'mahalapye-prison-staff-housing',
    title: 'Mahalapye Prison Staff Housing Refurbishment',
    category: 'maintenance',
    location: 'Mahalapye',
    client: 'DBES, Ministry of Infrastructure',
    parties: [{ role: 'Employer', name: 'DBES Head Office, Ministry of Infrastructure' }],
    summary:
      'Refurbishment and maintenance of 107 prison staff houses and 8 double-storey flats, including water reticulation and stormwater channels.',
    scope: [
      'Refurbishment of 107 prison staff houses',
      'Refurbishment of 8 no. double-storey flats',
      'Water reticulation',
      'Construction of stormwater channels',
    ],
    status: 'Completed October 2020',
    value: 'P13.77 million',
    images: [{ ...images.buildingWorks, caption: 'Representative image of housing refurbishment' }],
    serviceIds: ['building', 'drainage'],
    publication: 'published',
  },
  {
    id: 'orapa-frd-site-maintenance',
    title: 'Site Establishment Maintenance, Orapa FRD',
    category: 'maintenance',
    location: 'Orapa, Blue Area Fines Residual Disposal (FRD)',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary: 'Site establishment maintenance and ancillary small works at the Blue Area Fines Residual Disposal.',
    scope: ['Site establishment maintenance', 'Ancillary small works'],
    period: 'October 2018 – February 2020 (18 months)',
    status: 'Completed September 2020',
    value: 'P7.0 million',
    images: [{ ...images.tipperTrucks, caption: 'Representative image of site plant' }],
    serviceIds: ['civil'],
    publication: 'published',
  },
  {
    id: 'sos-francistown-renovations',
    title: 'SOS Francistown Renovations',
    category: 'maintenance',
    location: 'Francistown',
    client: 'SOS Francistown',
    parties: [{ role: 'Client', name: 'SOS Francistown' }],
    summary:
      'Foundation underpinning, building and civil renovation works, carpentry and plumbing, plus stormwater connected to the council network.',
    scope: [
      'Foundation underpinning',
      'Building and civil works renovations',
      'Carpentry and plumbing works',
      'Stormwater construction connecting to the council network',
    ],
    status: 'Completed August 2014',
    value: 'P4,100,000.00',
    images: [{ ...images.scaffolding, caption: 'Representative image of renovation works' }],
    serviceIds: ['building', 'drainage'],
    publication: 'published',
  },
];
